// components/CreateBroadbandInfoModal.jsx
import React, { useState } from "react";
import { X, Search } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateBroadbandInfoModal = ({ account, project, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    accountId: account?.id || "",
    projectId: project?.id || "",
    broadbandSupplier: "--None--",
    type: "--None--",
    expectedSpeeds: "",
    wan: "",
    localIp: "",
    dslUsername: "",
    dslPassword: "",
    lineNumber: "",
    notes: "",
    requestedDate: "",
    acceptedDate: "",
    broadbandInstalled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAccountSearch, setShowAccountSearch] = useState(false);

  const broadbandSupplierOptions = [
    "--None--",
    "Gamma",
    "Geocom",
    "Openreach",
    "Glide",
    "ITS",
    "Eclipse",
    "Elevate",
    "Toob",
    "Giacom",
    "G.Network",
  ];

  const typeOptions = [
    "--None--",
    "ADSL",
    "SOGEA",
    "FTTP",
    "FIBRE LIGHT",
    "FIBRE BRIGHT",
    "ETHERNET",
    "CITY FIBRE",
    "STARLINK",
    "Mobile 4G",
    "Mobile 5G",
    "Voice Router",
    "Data Router",
    "Combined Router",
    "Customer Owned",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.accountId) {
      setError("Account is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        broadbandSupplier:
          formData.broadbandSupplier === "--None--"
            ? null
            : formData.broadbandSupplier,
        type: formData.type === "--None--" ? null : formData.type,
        projectId: formData.projectId || null,
      };

      await apiService.createBroadbandInfo(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating broadband info:", err);
      setError(
        err.response?.data?.message || "Failed to create broadband information",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDarkMode
      ? "bg-[#0C0E12] border-gray-700 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `text-sm pt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`;

  const sectionBgClass = `px-4 py-2 mb-4 rounded ${
    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
  }`;

  const sectionHeadingClass = `text-sm font-semibold ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            New Broadband Information
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="broadbandInfoForm">
            {/* Information Section */}
            <div className={sectionBgClass}>
              <h3 className={sectionHeadingClass}>Information</h3>
            </div>

            <div className="space-y-4">
              {/* BI Ref */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>BI Ref</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value="Auto-generated on save"
                    disabled
                    className={`w-full px-3 py-2 border rounded cursor-not-allowed ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700 text-gray-500"
                        : "bg-gray-100 border-gray-300 text-gray-400"
                    }`}
                  />
                </div>
              </div>

              {/* Account */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>
                  <span className="text-red-500">*</span> Account
                </label>
                <div className="col-span-2">
                  {account ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 border border-blue-500/30 rounded flex-1">
                        <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-white"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
                          </svg>
                        </div>
                        <span className="text-sm text-white">
                          {account.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange("accountId", "")}
                        className={`px-3 py-2 text-sm rounded border transition-colors ${
                          isDarkMode
                            ? "text-gray-300 hover:bg-gray-700 border-gray-700"
                            : "text-gray-600 hover:bg-gray-100 border-gray-300"
                        }`}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search Accounts..."
                        className={`w-full px-3 py-2 pr-10 border border-red-500/50 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode
                            ? "bg-[#0C0E12] text-white placeholder-gray-500"
                            : "bg-white text-gray-900 placeholder-gray-400"
                        }`}
                        onFocus={() => setShowAccountSearch(true)}
                      />
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <p className="text-xs text-red-400 mt-1">
                        Complete this field.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Broadband Supplier */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Broadband Supplier</label>
                <div className="col-span-2">
                  <div className="relative">
                    <select
                      value={formData.broadbandSupplier}
                      onChange={(e) =>
                        handleChange("broadbandSupplier", e.target.value)
                      }
                      className={`${inputClass} appearance-none`}
                    >
                      {broadbandSupplierOptions.map((supplier) => (
                        <option key={supplier} value={supplier}>
                          {supplier}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Type */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Type</label>
                <div className="col-span-2">
                  <div className="relative">
                    <select
                      value={formData.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                      className={`${inputClass} appearance-none`}
                    >
                      {typeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expected Speeds */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Expected Speeds</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.expectedSpeeds}
                    onChange={(e) =>
                      handleChange("expectedSpeeds", e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              {/* WAN */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>WAN</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.wan}
                    onChange={(e) => handleChange("wan", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Local IP */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Local IP</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.localIp}
                    onChange={(e) => handleChange("localIp", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* DSL Username */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>DSL Username</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.dslUsername}
                    onChange={(e) =>
                      handleChange("dslUsername", e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              {/* DSL Password */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>DSL Password</label>
                <div className="col-span-2">
                  <input
                    type="password"
                    value={formData.dslPassword}
                    onChange={(e) =>
                      handleChange("dslPassword", e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Line Number */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Line Number</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.lineNumber}
                    onChange={(e) => handleChange("lineNumber", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Notes</label>
                <div className="col-span-2">
                  <textarea
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
            </div>

            {/* Project / Broadband Order Information Section */}
            <div className={`${sectionBgClass} mt-6`}>
              <h3 className={sectionHeadingClass}>
                Project / Broadband Order Information
              </h3>
            </div>

            <div className="space-y-4">
              {/* Project */}
              {project && (
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label className={labelClass}>Project</label>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 border border-purple-500/30 rounded">
                      <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                        </svg>
                      </div>
                      <span className="text-sm text-white">
                        {project.projectName}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Requested Date */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Requested Date</label>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={formData.requestedDate}
                    onChange={(e) =>
                      handleChange("requestedDate", e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Accepted Date */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Accepted Date</label>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={formData.acceptedDate}
                    onChange={(e) =>
                      handleChange("acceptedDate", e.target.value)
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Broadband Installed */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Broadband Installed</label>
                <div className="col-span-2">
                  <input
                    type="checkbox"
                    checked={formData.broadbandInstalled}
                    onChange={(e) =>
                      handleChange("broadbandInstalled", e.target.checked)
                    }
                    className={`w-4 h-4 rounded border-gray-700 text-blue-600 focus:ring-blue-500 ${
                      isDarkMode ? "bg-[#0C0E12]" : "bg-white"
                    }`}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-100"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              handleSubmit(new Event("submit"));
            }}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-100"
            }`}
          >
            Save & New
          </button>
          <button
            type="submit"
            form="broadbandInfoForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Required Information Notice */}
        <div
          className={`px-6 py-2 border-t ${
            isDarkMode
              ? "bg-gray-800/30 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <p
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <span className="text-red-500">*</span> = Required Information
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateBroadbandInfoModal;
